import ClaimsService from "@/api/services/claims";
import { toCamelCase, toSnakeCase } from "@/utils/utils";
import { downloadFromUrl } from "@estaly/ui";
import { parseDate } from "@/utils/date_formatter";

const COST_ITEM_FIELDS = ["type", "description", "unitPriceBeforeTax", "unitPrice", "quantity"];

const serializeCostItems = (costItems) =>
  costItems.map((costItem) =>
    toSnakeCase(Object.fromEntries(COST_ITEM_FIELDS.map((field) => [field, costItem[field]]))),
  );

const CLAIM_STATUSES = [
  "pending",
  "to_be_approved",
  "processing",
  "waiting_for_quotation",
  "waiting_for_customer_quotation",
  "quotation_to_be_approved",
  "quotation_refused",
  "waiting_for_invoice",
  "invoice_to_be_approved",
  "invoice_refused",
  "waiting_for_resolution",
  "waiting_for_customer_iban",
  "waiting_for_return_appointment",
  "pickup_scheduled",
  "waiting_for_clients_bank_details",
  "pending_deductible_payment",
  "pending_final_settlement",
  "pending_deductible_payment_and_final_settlement",
  "pending_additional_info",
  "resolved",
  "cancelled",
  "refused",
];

const initialState = {
  currentClaim: null,
  selectedFilters: {
    status: [],
    organizations: [],
    stores: [],
    ticketStatus: [],
    category: [],
    claimType: [],
    insurerContractGroupReference: [],
    assignee: [],
  },
  claims: [],
  totalCount: null,
  totalPages: null,
  keywords: "",
  order: "desc",
  inboxCounts: { unseen: 0, mineOpen: 0, unassignedOpen: 0 },
};

export default {
  namespaced: true,

  state: initialState,

  getters: {
    statuses: () => () => {
      return CLAIM_STATUSES;
    },

    isActiveClaim: () => (claim) => {
      const closedStatuses = ["resolved", "cancelled", "refused"];
      return !closedStatuses.includes(claim?.status?.toLowerCase());
    },

    claims: (state) => {
      return state.claims;
    },

    totalCount: (state) => {
      return state.totalCount;
    },

    totalPages: (state) => {
      return state.totalPages;
    },

    claimOpen: (state, getters) => {
      return getters.claimNotResolved && !getters.claimRefused && !getters.claimCancelled;
    },

    claimResolved: (state) => {
      return state.currentClaim?.status === "resolved";
    },

    claimNotResolved: (state) => {
      return state.currentClaim?.status !== "resolved";
    },

    claimRefused: (state) => {
      return state.currentClaim?.status === "refused";
    },

    claimPendingAdditionalInfo: (state) => {
      return state.currentClaim?.status === "pending_additional_info";
    },

    claimCancelled: (state) => {
      return state.currentClaim?.status === "cancelled";
    },

    claimApprovalRequired: (state) => {
      return state.currentClaim?.status === "to_be_approved";
    },

    claimPending: (state) => {
      return state.currentClaim?.status === "pending";
    },

    claimProcessing: (state) => {
      return state.currentClaim?.status === "processing";
    },

    claimWaitingForQuotation: (state) => {
      return state.currentClaim?.status === "waiting_for_quotation";
    },

    claimWaitingForInvoice: (state) => {
      return state.currentClaim?.status === "waiting_for_invoice";
    },

    quotationApprovalRequired: (state) => {
      return state.currentClaim?.status === "quotation_to_be_approved";
    },

    invoiceApprovalRequired: (state) => {
      return state.currentClaim?.status === "invoice_to_be_approved";
    },

    deductiblePaymentRequired: (state) => {
      return state.currentClaim?.status === "pending_deductible_payment";
    },

    finalSettlementPaymentRequired: (state) => {
      return state.currentClaim?.status === "pending_final_settlement";
    },

    finalSettlementAndDeductiblePaymentRequired: (state) => {
      return state.currentClaim?.status === "pending_deductible_payment_and_final_settlement";
    },

    claimWaitingForResolution: (state) => {
      return state.currentClaim?.status === "waiting_for_resolution";
    },

    claimPickupScheduled: (state) => {
      return state.currentClaim?.status === "pickup_scheduled";
    },

    claimWaitingForCustomerIban: (state) => {
      return state.currentClaim?.status === "waiting_for_customer_iban";
    },

    repairHandlingType: (state) => {
      return state.currentClaim?.handlingType === "repair";
    },

    replacementHandlingType: (state) => {
      return state.currentClaim?.handlingType === "replacement";
    },

    accessoryReplacementHandlingType: (state) => {
      return state.currentClaim?.handlingType === "accessory_replacement";
    },

    voucherHandlingType: (state) => {
      return state.currentClaim?.handlingType === "voucher";
    },

    reimbursementHandlingType: (state) => {
      return state.currentClaim?.handlingType === "reimbursement";
    },

    showVoucherCreate: (state, getters) => {
      return getters.claimProcessing && getters.voucherHandlingType && !state.currentClaim.voucher;
    },

    anyRequiredAction: (state, getters) => {
      return (
        getters.invoiceApprovalRequired ||
        getters.quotationApprovalRequired ||
        getters.claimApprovalRequired ||
        getters.claimPending ||
        getters.claimWaitingForResolution ||
        getters.claimPendingAdditionalInfo
      );
    },

    hasRequiredAction: () => (claim) => {
      const status = claim.status;
      return (
        status === "invoice_to_be_approved" ||
        status === "quotation_to_be_approved" ||
        status === "to_be_approved" ||
        status === "pending" ||
        status === "waiting_for_resolution"
      );
    },

    storeHandleRepair: () => (claim) => {
      return claim?.store?.repairWorkshop;
    },

    shouldCaptureReplacementProduct: () => (claim) => {
      const planSale = claim?.planSale;
      return (
        claim?.handlingType === "replacement" &&
        planSale?.status === "active" &&
        planSale?.productless === false &&
        planSale?.composedProductType === false
      );
    },

    repairOrReplacement: () => (handlingType) => {
      return ["repair", "replacement", "accessory_replacement"].includes(handlingType);
    },

    repair: () => (handlingType) => {
      return handlingType === "repair";
    },

    handlingTypeOptions() {
      return [
        {
          label: "Réparation",
          value: "repair",
        },
        {
          label: "Remplacement",
          value: "replacement",
        },
        {
          label: "Remplacement d'accessoires",
          value: "accessory_replacement",
        },
        {
          label: "Remboursement",
          value: "reimbursement",
        },
        {
          label: "Bon d'achat",
          value: "voucher",
        },
      ];
    },

    refundedEntityOptions() {
      return [
        {
          label: "Marchand",
          value: "merchant",
        },
        {
          label: "Client",
          value: "customer",
        },
      ];
    },

    claimHandlerOptions() {
      return [
        {
          label: "Marchand",
          value: "merchant",
        },
        {
          label: "Marchand externe",
          value: "external_merchant",
        },
        /*
        {
          label: "Partenaire d'Estaly",
          value: "estaly_partner",
        },
        */
      ];
    },

    claimTypes: () => () => {
      return [
        "theft",
        "partial_theft",
        "breakage",
        "breakdown",
        "loss",
        "error_margin",
        "rips",
        "stains",
        "burn",
        "oxidation",
        "scratch",
        "chip",
        "other",
      ];
    },

    claimTicketStatusOpen:
      (state) =>
      (claim = state.currentClaim) => {
        return claim?.ticketStatus === "open";
      },

    claimTicketStatusPaused:
      (state) =>
      (claim = state.currentClaim) => {
        return claim?.ticketStatus === "paused";
      },

    ticketStatusStyle: () => (ticketStatus) => {
      switch (ticketStatus) {
        case "open":
          return "bg-green-100 text-green-800";
        case "paused":
          return "bg-indigo-100 text-indigo-800";
        case "closed":
          return "bg-red-100 text-red-800";
        default:
          return "bg-gray-100 text-gray-800"; // style par défaut pour les statuts inconnus
      }
    },

    handlingTypeStyle: () => (handlingType) => {
      switch (handlingType) {
        case "repair":
          return "bg-blue-500";
        case "replacement":
          return "bg-green-500";
        case "accessory_replacement":
          return "bg-yellow-500";
        case "reimbursement":
          return "bg-purple-500";
        case "voucher":
          return "bg-pink-500";
        default:
          return "bg-gray-500";
      }
    },

    statusStyle: () => (status) => {
      switch (status) {
        // Statuts rouges (refus et annulations)
        case "cancelled":
        case "refused":
        case "quotation_refused":
        case "invoice_refused":
          return "bg-red-100 text-red-800";

        // Statuts orange (en attente d'approbation)
        case "to_be_approved":
        case "quotation_to_be_approved":
        case "invoice_to_be_approved":
          return "bg-indigo-100 text-indigo-800";

        // Statuts bleus (en cours de traitement/attente)
        case "processing":
        case "pending_deductible_payment":
        case "pending_deductible_payment_and_final_settlement":
        case "pending_final_settlement":
        case "waiting_for_quotation":
        case "waiting_for_customer_quotation":
        case "waiting_for_invoice":
        case "waiting_for_clients_bank_details":
        case "waiting_for_resolution":
        case "waiting_for_customer_iban":
        case "waiting_for_return_appointment":
        case "pickup_scheduled":
          return "bg-blue-100 text-blue-800";

        // Statuts verts (approuvés/résolus)
        case "resolved":
        case "approved":
        case "clients_bank_details_uploaded":
        case "voucher_sent":
        case "pickup_completed":
        case "quotation_approved":
        case "invoice_approved":
          return "bg-green-100 text-green-800";

        // Statut jaune
        case "pending":
          return "bg-yellow-100 text-yellow-800";

        // Statut gris
        case "pending_additional_info":
          return "bg-gray-100 text-gray-800";

        // Statut par défaut
        default:
          return "bg-gray-100 text-gray-800";
      }
    },

    productPurchasedLessThanOneYearAgo: () => (claimProduct) => {
      if (!claimProduct) return false;

      const purchaseDate = parseDate(claimProduct.purchaseDate);
      if (!purchaseDate) return false;

      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

      return purchaseDate > oneYearAgo;
    },
  },

  mutations: {
    setInboxCounts(state, inboxCounts) {
      state.inboxCounts = inboxCounts;
    },

    setCurrentClaim(state, currentClaim) {
      state.currentClaim = currentClaim;
    },

    setClaims(state, { claims, totalCount, totalPages }) {
      state.claims = claims;
      state.totalCount = totalCount;
      state.totalPages = totalPages;
    },

    setFilter(state, { filterName, filterOptions }) {
      state.selectedFilters[filterName] = filterOptions;
    },

    setKeywords(state, keywords) {
      state.keywords = keywords;
    },

    setOrder(state, order) {
      state.order = order;
    },
  },

  actions: {
    async fetch({ commit, dispatch }, friendlyId) {
      const response = await ClaimsService.fetch(friendlyId, [
        "store",
        "customer",
        "customer.iban",
        "claim_declarations",
        "voucher",
        "claim_historical_datas",
        "claim_limit",
        "claim_deductible",
        "final_settlement",
        "plan_sale",
        "invoice",
        "invoice.alerts",
        "quotation",
        "quotation.alerts",
        "optional_data",
        "claim_product",
        "unfixeable_claim_product",
        "unfixeable_claim_products",
        "plan_sale.original_plan_sale",
        "fraud_suspicion_flagged_by",
        "assignee",
      ]);
      if (!response?.success) {
        dispatch("resetCurrentClaim");
        return response;
      }

      const claim = toCamelCase(response.data);
      commit("setCurrentClaim", claim);
      // Opening a claim marks it as seen by its assignee, without any broadcast.
      dispatch("fetchInboxCounts");
      return response;
    },

    async fetchInboxCounts({ commit }) {
      const response = await ClaimsService.fetchInboxCounts();

      if (!response?.success) return response;

      commit("setInboxCounts", toCamelCase(response.data));

      return response;
    },

    async fetchIndex(
      { commit },
      {
        page,
        keywords,
        organizations,
        stores,
        status,
        ticketStatus,
        planSaleFriendlyId,
        category,
        claimType,
        insurerContractGroupReference,
        assignee,
        order,
      },
    ) {
      const response = await ClaimsService.fetchIndex({
        organizations,
        stores,
        status,
        page,
        keywords,
        ticketStatus,
        planSaleFriendlyId,
        category,
        claimType,
        insurerContractGroupReference,
        assignee,
        order,
      });

      if (!response?.success) return response;

      const responseData = toCamelCase(response.data);

      commit("setClaims", {
        claims: responseData.claims,
        totalCount: responseData.page.totalCount,
        totalPages: responseData.page.totalPages,
      });

      return response;
    },

    updateSearch({ commit }, { searchString }) {
      commit("setKeywords", searchString);
    },

    updateOrder({ commit }, order) {
      commit("setOrder", order);
    },

    updateFilter({ commit }, { filterName, filterOptions }) {
      commit("setFilter", { filterName, filterOptions });
    },

    async exportClaimData({ state }) {
      const response = await ClaimsService.exportClaimData(state.currentClaim.id);
      if (!response?.success) return response;
      downloadFromUrl(response.data.url, response.data.filename);
    },

    async bulkExportClaims(_, params) {
      return await ClaimsService.bulkExportClaims(params);
    },

    async approveClaim({ state, dispatch }, { internalNotes, customerMessage }) {
      const response = await ClaimsService.approveClaim(state.currentClaim.id, {
        internalNotes,
        customerMessage,
      });

      if (!response?.success) return response;

      // Attendu pour que l'étape suivante (prise en charge) soit montée sur le sinistre rechargé.
      await dispatch("fetch", state.currentClaim.friendlyId);

      return response;
    },

    async refuseClaim({ state, dispatch }, { internalNotes, customerMessage }) {
      const response = await ClaimsService.refuseClaim(state.currentClaim.id, {
        internalNotes,
        customerMessage,
      });

      if (!response?.success) return response;

      dispatch("fetch", state.currentClaim.friendlyId);

      return response;
    },

    async updateClaim({ state, dispatch }, params) {
      const response = await ClaimsService.updateClaim(state.currentClaim.id, params);

      if (!response?.success) return response;

      dispatch("fetch", state.currentClaim.friendlyId);

      return response;
    },

    async updateEstalyManagerNotes(_, { id, internalNotes }) {
      return await ClaimsService.updateEstalyManagerNotes(id, { internalNotes });
    },

    async specifyHandlingType({ state, dispatch }, params) {
      const response = await ClaimsService.specifyHandlingType(state.currentClaim.id, params);

      if (!response?.success) return response;

      dispatch("fetch", state.currentClaim.friendlyId);

      return response;
    },

    async uploadQuotation({ state, dispatch }, { costItems, file, productState }) {
      let formData = new FormData();
      formData.append("file", file);

      costItems
        .map((item) => toSnakeCase(item))
        .forEach((item, index) => {
          Object.keys(item).forEach((key) => {
            formData.append(`cost_items[${index}][${key}]`, item[key]);
          });
        });

      if (productState) {
        formData.append("product_state", productState);
      }

      const response = await ClaimsService.uploadQuotation(state.currentClaim.id, formData);

      if (!response?.success) return response;

      dispatch("fetch", state.currentClaim.friendlyId);

      return response;
    },

    declareIrreparable: async function ({ state, dispatch }, { message, reason, irreparabilityCertificate }) {
      let formData = new FormData();
      if (irreparabilityCertificate) formData.append("irreparability_certificate", irreparabilityCertificate);
      formData.append("message", message);
      formData.append("reason", reason);

      const response = await ClaimsService.declareIrreparable(state.currentClaim.id, formData);

      if (!response?.success) return response;

      return dispatch("fetch", state.currentClaim.friendlyId);
    },

    async updateQuotationCostItems(_, { quotationId, costItems }) {
      return await ClaimsService.updateQuotationCostItems(quotationId, serializeCostItems(costItems));
    },

    async updateInvoiceCostItems(_, { invoiceId, costItems }) {
      return await ClaimsService.updateInvoiceCostItems(invoiceId, serializeCostItems(costItems));
    },

    async approveQuotation({ state, dispatch }) {
      const response = await ClaimsService.approveQuotation(state.currentClaim.id);

      if (!response?.success) return response;

      dispatch("fetch", state.currentClaim.friendlyId);

      return response;
    },

    async refuseQuotation({ state, dispatch }, params) {
      const response = await ClaimsService.refuseQuotation(state.currentClaim.id, params);

      if (!response?.success) return response;

      return dispatch("fetch", state.currentClaim.friendlyId);
    },

    async generateVoucher({ state, dispatch }) {
      const response = await ClaimsService.generateVoucher(state.currentClaim.id);

      if (!response?.success) return response;

      dispatch("fetch", state.currentClaim.friendlyId);

      return response;
    },

    async resetHandlingType({ state, dispatch }) {
      const response = await ClaimsService.resetHandlingType(state.currentClaim.id);

      if (!response?.success) return response;

      dispatch("fetch", state.currentClaim.friendlyId);

      return response;
    },

    async uploadInvoice({ state, dispatch }, { costItems, file, replacementProduct }) {
      let formData = new FormData();
      formData.append("file", file);

      costItems
        .map((item) => toSnakeCase(item))
        .forEach((item, index) => {
          Object.keys(item).forEach((key) => {
            formData.append(`cost_items[${index}][${key}]`, item[key]);
          });
        });

      if (replacementProduct) {
        Object.entries(toSnakeCase(replacementProduct)).forEach(([key, value]) => {
          formData.append(`replacement_product[${key}]`, value ?? "");
        });
      }

      const response = await ClaimsService.uploadInvoice(state.currentClaim.id, formData);

      if (!response?.success) return response;

      dispatch("fetch", state.currentClaim.friendlyId);

      return response;
    },

    async approveInvoice({ state, dispatch }, { replacementProduct } = {}) {
      const response = await ClaimsService.approveInvoice(state.currentClaim.id, { replacementProduct });

      if (!response?.success) return response;

      return dispatch("fetch", state.currentClaim.friendlyId);
    },

    async refuseInvoice({ state, dispatch }, { refusalReason }) {
      const response = await ClaimsService.refuseInvoice(state.currentClaim.id, { refusalReason });

      if (!response?.success) return response;

      return dispatch("fetch", state.currentClaim.friendlyId);
    },

    async resolveClaim({ state, dispatch }, { refundedEntity, refundedAmount, sendTrustpilotLink }) {
      const response = await ClaimsService.resolveClaim(state.currentClaim.id, {
        refundedEntity,
        refundedAmount,
        sendTrustpilotLink,
      });

      if (!response?.success) return response;

      dispatch("fetch", state.currentClaim.friendlyId);

      return response;
    },

    async cancelClaim({ state, dispatch }, { customerMessage }) {
      const response = await ClaimsService.cancelClaim(state.currentClaim.id, { customerMessage });

      if (!response?.success) return response;

      dispatch("fetch", state.currentClaim.friendlyId);

      return response;
    },

    resetCurrentClaim({ commit }) {
      commit("setCurrentClaim", null);
    },

    async requestAdditionalInfo({ state, dispatch }, { declarationId, fields, fieldMessages }) {
      const response = await ClaimsService.requestAdditionalInfo(state.currentClaim.id, {
        declarationId,
        fields,
        fieldMessages,
      });

      if (!response?.success) return response;

      dispatch("fetch", state.currentClaim.friendlyId);

      return response;
    },

    async reopen({ state, dispatch }) {
      const response = await ClaimsService.reopen(state.currentClaim.id);

      if (!response?.success) return response;

      dispatch("fetch", state.currentClaim.friendlyId);

      return response;
    },

    async getPaymentUrl({ state }) {
      return await ClaimsService.getPaymentUrl(state.currentClaim.id);
    },

    async updateTicketStatus(
      { state, dispatch },
      { claim = state.currentClaim, ticketStatus, reopeningDate, refresh = true },
    ) {
      const response = await ClaimsService.updateTicketStatus(claim.id, { ticketStatus, reopeningDate });

      if (!response?.success) return response;

      if (refresh) dispatch("fetch", claim.friendlyId);

      return response;
    },

    async assign({ state, dispatch }, { assigneeId }) {
      const response = await ClaimsService.assign(state.currentClaim.friendlyId, assigneeId);

      if (!response?.success) return response;

      dispatch("fetch", state.currentClaim.friendlyId);

      return response;
    },

    async updateFraudSuspicion({ state, dispatch }, { fraudSuspicion, reason = null }) {
      const response = await ClaimsService.updateFraudSuspicion(state.currentClaim.id, { fraudSuspicion, reason });

      if (!response?.success) return response;

      dispatch("fetch", state.currentClaim.friendlyId);

      return response;
    },

    async extractCostItems({ state }, { file }) {
      if (!state.currentClaim?.id) {
        return { success: false, error: "missing_current_claim_id" };
      }
      return await ClaimsService.extractCostItems(state.currentClaim.id, file);
    },

    async extractReplacementProduct({ state }, { file }) {
      return await ClaimsService.extractReplacementProduct(state.currentClaim.id, file);
    },

    async analyzeDocumentForApproval({ state }, { file, documentKind }) {
      return await ClaimsService.analyzeDocumentForApproval(state.currentClaim.id, file, documentKind);
    },

    async fetchBillingInvoices(_, params) {
      return await ClaimsService.fetchClaimInvoices(params);
    },
    async markInvoicesAsPaid(_, claimInvoiceIds) {
      return await ClaimsService.markClaimInvoicesAsPaid(claimInvoiceIds);
    },
    async getInvoicesTransfers(_, claimInvoiceIds) {
      return await ClaimsService.getClaimInvoicesTransfers(claimInvoiceIds);
    },
  },
};
